package com.example.readingnotes.data.remote.oidc

import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonPrimitive

/**
 * OIDC Client for Authorization Code Flow with PKCE
 * Implements RFC 6749 (OAuth 2.0) and RFC 7636 (PKCE)
 */
class OidcClient(
    private val config: OidcConfig,
    private val httpClient: HttpClient,
) {
    private val json = Json { ignoreUnknownKeys = true }

    /**
     * Generate authorization URL with PKCE parameters
     *
     * @param stateHint Optional hint for state generation
     * @return AuthorizationUrlResult containing URL and state
     */
    suspend fun generateAuthorizationUrl(stateHint: String? = null): AuthorizationUrlResult {
        // Fetch OpenID configuration if endpoints not provided
        val openIdConfig = fetchOpenIdConfiguration()

        // Generate PKCE pair
        val pkcePair = generatePkcePair()

        // Generate state for CSRF protection
        val state = stateHint ?: generateState()

        // Build authorization URL
        val authUrl =
            buildAuthorizationUrl(
                authorizationEndpoint = openIdConfig.authorizationEndpoint,
                clientId = config.clientId,
                redirectUri = config.redirectUri,
                scope = config.scope,
                state = state,
                codeChallenge = pkcePair.codeChallenge,
            )

        return AuthorizationUrlResult(
            url = authUrl,
            state = state,
            codeVerifier = pkcePair.codeVerifier,
        )
    }

    /**
     * Exchange authorization code for tokens
     *
     * @param code Authorization code from callback
     * @param codeVerifier PKCE code verifier
     * @return TokenResponse with access token and user info
     */
    suspend fun exchangeCodeForTokens(
        code: String,
        codeVerifier: String,
    ): TokenResponse {
        val openIdConfig = fetchOpenIdConfiguration()

        val response: TokenResponseInternal =
            httpClient
                .post(openIdConfig.tokenEndpoint) {
                    parameter("grant_type", "authorization_code")
                    parameter("code", code)
                    parameter("redirect_uri", config.redirectUri)
                    parameter("client_id", config.clientId)
                    parameter("code_verifier", codeVerifier)
                    config.clientSecret?.let {
                        parameter("client_secret", it)
                    }
                }.body()

        // Decode JWT claims from access token (if available)
        val claims = decodeJwtClaims(response.accessToken)

        return TokenResponse(
            accessToken = response.accessToken,
            refreshToken = response.refreshToken,
            expiresIn = response.expiresIn,
            tokenType = response.tokenType,
            idToken = response.idToken,
            claims = claims,
        )
    }

    /**
     * Refresh access token using refresh token
     *
     * @param refreshToken Refresh token
     * @return TokenResponse with new access token
     */
    suspend fun refreshToken(refreshToken: String): TokenResponse {
        val openIdConfig = fetchOpenIdConfiguration()

        val response: TokenResponseInternal =
            httpClient
                .post(openIdConfig.tokenEndpoint) {
                    parameter("grant_type", "refresh_token")
                    parameter("refresh_token", refreshToken)
                    parameter("client_id", config.clientId)
                    config.clientSecret?.let {
                        parameter("client_secret", it)
                    }
                }.body()

        val claims = decodeJwtClaims(response.accessToken)

        return TokenResponse(
            accessToken = response.accessToken,
            refreshToken = response.refreshToken ?: refreshToken,
            expiresIn = response.expiresIn,
            tokenType = response.tokenType,
            idToken = response.idToken,
            claims = claims,
        )
    }

    /**
     * Revoke token (best-effort, endpoint may not be available)
     *
     * @param token Token to revoke
     * @param tokenTypeHint Type of token (access_token or refresh_token)
     */
    suspend fun revokeToken(
        token: String,
        tokenTypeHint: String = "refresh_token",
    ) {
        val openIdConfig = fetchOpenIdConfiguration()

        try {
            httpClient.post(openIdConfig.revocationEndpoint ?: return) {
                parameter("token", token)
                parameter("token_type_hint", tokenTypeHint)
                config.clientSecret?.let {
                    parameter("client_secret", it)
                }
            }
        } catch (e: Exception) {
            // Revocation is best-effort, don't fail if endpoint unavailable
        }
    }

    /**
     * Fetch OpenID Connect Discovery configuration
     */
    private suspend fun fetchOpenIdConfiguration(): OpenIdConfigInternal {
        if (config.authorizationEndpoint != null &&
            config.tokenEndpoint != null &&
            config.jwksUri != null
        ) {
            return OpenIdConfigInternal(
                issuer = config.issuer,
                authorizationEndpoint = config.authorizationEndpoint,
                tokenEndpoint = config.tokenEndpoint,
                jwksUri = config.jwksUri,
                revocationEndpoint = null,
            )
        }

        // Fetch from .well-known/openid-configuration
        val discoveryUrl = "${config.issuer.removeSuffix("/")}/.well-known/openid-configuration"
        val response: JsonObject = httpClient.get(discoveryUrl).body()

        return OpenIdConfigInternal(
            issuer = response["issuer"]?.jsonPrimitive?.content ?: config.issuer,
            authorizationEndpoint =
                response["authorization_endpoint"]?.jsonPrimitive?.content
                    ?: throw IllegalStateException("Missing authorization_endpoint"),
            tokenEndpoint =
                response["token_endpoint"]?.jsonPrimitive?.content
                    ?: throw IllegalStateException("Missing token_endpoint"),
            jwksUri =
                response["jwks_uri"]?.jsonPrimitive?.content
                    ?: throw IllegalStateException("Missing jwks_uri"),
            revocationEndpoint = response["revocation_endpoint"]?.jsonPrimitive?.content,
        )
    }

    /**
     * Build authorization URL with required parameters
     */
    private fun buildAuthorizationUrl(
        authorizationEndpoint: String,
        clientId: String,
        redirectUri: String,
        scope: String,
        state: String,
        codeChallenge: String,
    ): String {
        val params =
            buildString {
                append("response_type=code")
                append("&client_id=${encodeUrlParameter(clientId)}")
                append("&redirect_uri=${encodeUrlParameter(redirectUri)}")
                append("&scope=${encodeUrlParameter(scope)}")
                append("&state=${encodeUrlParameter(state)}")
                append("&code_challenge=${encodeUrlParameter(codeChallenge)}")
                append("&code_challenge_method=S256")
            }

        return if (authorizationEndpoint.contains("?")) {
            "$authorizationEndpoint&$params"
        } else {
            "$authorizationEndpoint?$params"
        }
    }

    /**
     * Decode JWT claims from access token (without verification)
     * For display purposes only - verification happens on backend
     */
    private fun decodeJwtClaims(token: String): JwtClaims {
        val parts = token.split(".")
        if (parts.size != 3) {
            return JwtClaims()
        }

        return try {
            val payload = decodeBase64Url(parts[1])
            val jsonObject = json.parseToJsonElement(payload) as? JsonObject
            JwtClaims(
                sub = jsonObject?.get("sub")?.jsonPrimitive?.content,
                email = jsonObject?.get("email")?.jsonPrimitive?.content,
                name = jsonObject?.get("name")?.jsonPrimitive?.content,
                preferredUsername = jsonObject?.get("preferred_username")?.jsonPrimitive?.content,
                activeTenantId = jsonObject?.get("active_tenant_id")?.jsonPrimitive?.content,
                tenantRole = jsonObject?.get("tenant_role")?.jsonPrimitive?.content,
            )
        } catch (e: Exception) {
            JwtClaims()
        }
    }

    private fun encodeUrlParameter(value: String): String =
        value
            .replace("%", "%25")
            .replace(" ", "%20")
            .replace("+", "%2B")
            .replace("/", "%2F")
            .replace("?", "%3F")
            .replace("#", "%23")
            .replace("&", "%26")
            .replace("=", "%3D")

    private fun decodeBase64Url(input: String): String {
        // Add padding if needed
        val padded = input + "=".repeat((4 - input.length % 4) % 4)
        // Replace URL-safe chars
        val base64 = padded.replace("-", "+").replace("_", "/")
        // Decode base64 (platform-specific implementation)
        return decodeBase64(base64)
    }
}

/**
 * Platform-specific Base64 decoding
 */
expect fun decodeBase64(input: String): String

/**
 * Result from generateAuthorizationUrl
 */
data class AuthorizationUrlResult(
    val url: String,
    val state: String,
    val codeVerifier: String,
)

/**
 * Token response from token endpoint
 */
data class TokenResponse(
    val accessToken: String,
    val refreshToken: String?,
    val expiresIn: Int,
    val tokenType: String,
    val idToken: String?,
    val claims: JwtClaims,
)

/**
 * JWT claims extracted from token
 */
data class JwtClaims(
    val sub: String? = null,
    val email: String? = null,
    val name: String? = null,
    val preferredUsername: String? = null,
    val activeTenantId: String? = null,
    val tenantRole: String? = null,
)

// Internal DTOs for token endpoint response
@Serializable
private data class TokenResponseInternal(
    @SerialName("access_token")
    val accessToken: String,
    @SerialName("refresh_token")
    val refreshToken: String? = null,
    @SerialName("expires_in")
    val expiresIn: Int,
    @SerialName("token_type")
    val tokenType: String,
    @SerialName("id_token")
    val idToken: String? = null,
)

// Internal DTO for OpenID configuration
private data class OpenIdConfigInternal(
    val issuer: String,
    val authorizationEndpoint: String,
    val tokenEndpoint: String,
    val jwksUri: String,
    val revocationEndpoint: String?,
)
